import React, { useState, useEffect } from 'react';
import { AlertTriangle, Clock, CheckCircle, XCircle, Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import api from '../utils/api';
import { toast } from 'sonner';

const Disputes = () => {
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [newDispute, setNewDispute] = useState({ booking_id: '', reason: '', description: '' });

  useEffect(() => {
    fetchDisputes();
    fetchBookings();
  }, []);

  const fetchDisputes = async () => {
    try {
      const response = await api.get('/api/disputes');
      setDisputes(response.data);
    } catch (error) {
      setDisputes([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async () => {
    try {
      const response = await api.get('/api/bookings');
      setBookings(response.data);
    } catch (error) {
      console.log('Failed to fetch bookings');
    }
  };

  const createDispute = async () => {
    if (!newDispute.booking_id || !newDispute.reason || !newDispute.description) {
      toast.error('Please fill in all fields');
      return;
    }
    try {
      const params = new URLSearchParams({
        booking_id: newDispute.booking_id,
        reason: newDispute.reason,
        description: newDispute.description
      });
      await api.post(`/api/disputes?${params}`);
      toast.success('Dispute submitted successfully');
      setShowCreate(false);
      setNewDispute({ booking_id: '', reason: '', description: '' });
      fetchDisputes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create dispute');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open': return <AlertTriangle className="w-5 h-5 text-yellow-400" />;
      case 'under_review': return <Clock className="w-5 h-5 text-blue-400" />;
      case 'resolved': return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'closed': return <XCircle className="w-5 h-5 text-gray-400" />;
      default: return <AlertTriangle className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'under_review': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'resolved': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'closed': return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  return (
    <div className="relative z-10 min-h-screen pt-24 pb-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-8 h-8 text-yellow-400" />
            <h1 className="text-3xl font-bold text-white">Dispute Center</h1>
          </div>
          <Button onClick={() => setShowCreate(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Open Dispute
          </Button>
        </div>

        <div className="bg-white/5 rounded-xl p-4 mb-8">
          <p className="text-gray-400 text-sm">
            Having an issue with a booking? Submit a dispute and our team will review it within 24-48 hours.
            Please provide as much detail as possible to help us resolve your issue quickly.
          </p>
        </div>

        {/* Create Dispute Modal */}
        {showCreate && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 rounded-xl p-6 w-full max-w-md border border-gray-700">
              <h2 className="text-xl font-bold text-white mb-4">Open a Dispute</h2>
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Select Booking</label>
                  <select
                    value={newDispute.booking_id}
                    onChange={e => setNewDispute({ ...newDispute, booking_id: e.target.value })}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg p-2 text-white"
                  >
                    <option value="">Select a booking...</option>
                    {bookings.map(booking => (
                      <option key={booking.booking_id} value={booking.booking_id}>
                        {booking.event_date} - ${booking.total_cost?.toFixed(2)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Reason</label>
                  <select
                    value={newDispute.reason}
                    onChange={e => setNewDispute({ ...newDispute, reason: e.target.value })}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg p-2 text-white"
                  >
                    <option value="">Select reason...</option>
                    <option value="no_show">Artist No Show</option>
                    <option value="quality">Quality Issues</option>
                    <option value="overcharge">Overcharged</option>
                    <option value="communication">Communication Problems</option>
                    <option value="cancellation">Cancellation Issue</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Description</label>
                  <textarea
                    value={newDispute.description}
                    onChange={e => setNewDispute({ ...newDispute, description: e.target.value })}
                    placeholder="Please describe your issue in detail..."
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg p-3 text-white min-h-[120px]"
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <Button onClick={() => setShowCreate(false)} variant="outline" className="flex-1">
                    Cancel
                  </Button>
                  <Button onClick={createDispute} className="flex-1">
                    Submit Dispute
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Disputes List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto"></div>
          </div>
        ) : disputes.length === 0 ? (
          <div className="text-center py-12 bg-white/5 rounded-xl">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <p className="text-gray-400">No disputes found</p>
            <p className="text-gray-500 text-sm mt-2">That's great! All your bookings are running smoothly.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {disputes.map(dispute => (
              <div
                key={dispute.dispute_id}
                className={`p-6 rounded-xl border ${getStatusColor(dispute.status)} bg-opacity-50`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {getStatusIcon(dispute.status)}
                    <div>
                      <h3 className="font-semibold text-white capitalize">
                        {dispute.reason?.replace('_', ' ')}
                      </h3>
                      <p className="text-sm text-gray-400">
                        Booking: {dispute.booking_id}
                      </p>
                    </div>
                  </div>
                  <span className={`text-xs px-3 py-1 rounded-full ${getStatusColor(dispute.status)}`}>
                    {dispute.status?.replace('_', ' ')}
                  </span>
                </div>
                <p className="text-gray-300 mb-4">{dispute.description}</p>
                {dispute.resolution && (
                  <div className="bg-white/5 rounded-lg p-4 mt-4">
                    <p className="text-sm text-gray-400 mb-1">Resolution</p>
                    <p className="text-white">{dispute.resolution}</p>
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-4">
                  Opened: {new Date(dispute.created_at).toLocaleString()}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Disputes;